import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { AyaConversationsService } from 'src/app/core/services/aya-conversations.service';
import { ChatConversation, ChatMessage } from 'src/app/core/models/chat-history.model';
import {
  AyaConsultMode,
  AyaRunDto,
  DEFAULT_AYA_CONSULT_MODE,
} from 'src/app/core/models/aya-conversation.model';
import { SidebarStateService } from 'src/app/core/services/sidebar-state.service';
import { formatAyaProse } from 'src/app/core/utils/aya-markdown.util';
import {
  advanceAyaPhases,
  AyaProgressPhase,
  AyaSearchStep,
  initialAyaPhases,
  isRunLifecycleStatus,
  mapPhaseKey,
} from 'src/app/core/utils/aya-progress.util';
import {
  AyaMessageBlocks,
  scrollToSourceCard,
  splitAyaBlocks,
} from 'src/app/core/utils/aya-sources.util';
import {
  ChatListItem,
  isServerMessageId,
  withDayHeaders,
} from 'src/app/core/utils/chat-day-headers.util';

const CLINICIAN_QUERY_POOL = [
  'Resistant hypertension in CKD 3',
  'SGLT2i in HFpEF — who benefits',
  'DOAC dosing in CrCl 20 mL/min',
  'Statin intolerance — next line',
  'Empiric therapy for febrile neutropenia',
  'GLP-1 perioperative hold interval',
  'Anticoagulation in AF with recent GI bleed',
  'Metformin threshold in declining eGFR',
  'Hyperkalemia on MRA — management',
  'Latent TB regimen with hepatic disease',
  'Steroid taper in polymyalgia rheumatica',
  'Semaglutide vs tirzepatide — head to head data',
  'Post-MI beta blocker duration',
  'Iron repletion target in HF',
  'Gout flare in CKD 4',
  'DDx: 54F pleuritic chest pain, normal ECG',
  'DDx: 3 weeks cough + fever, failed azithromycin',
  'DDx: new microcytic anemia in a 68M',
  'DDx: transaminitis 5x ULN, no jaundice',
  'Differential for unilateral leg swelling',
];

const CONSULT_MODE_OPTIONS: { value: AyaConsultMode; label: string }[] = [
  { value: 'quick', label: 'Quick' },
  { value: 'standard', label: 'Thoughtful' },
  { value: 'deep', label: 'Research' },
];

@Component({
  selector: 'app-ai-chat-bot',
  templateUrl: './ai-chat-bot.component.html',
  styleUrls: ['./ai-chat-bot.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class AiChatBotComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chatContainer', { static: false }) private chatContainer: ElementRef;
  userMessage: string = '';
  userName: string = '';
  messages: ChatMessage[] = [];
  chatListItems: ChatListItem[] = [];
  isLoading: boolean = false;
  showWelcome: boolean = true;
  currentConversationId: string | null = null;
  allConversations = false;
  searchQuery: string = '';
  
  // File upload and dropdown properties
  showDropdown: boolean = false;
  attachments: File[] = [];
  attachmentPreviews: string[] = [];
  
  // Current conversation period
  currentConversationPeriod: string = 'Today';
  currentConversationTitle: string = '';
  
  // Typewriter effect properties
  typingMessages: Map<string, string> = new Map(); // messageId -> displayed content
  isTyping: boolean = false;
  
  // Message actions properties
  showShareModal: boolean = false;
  sharedMessage: ChatMessage | null = null;
  likedMessages: Set<string> = new Set();
  dislikedMessages: Set<string> = new Set();
  showCopyToast: boolean = false;
  copyToastTimeout: any;
  
  // Context menu properties
  showContextMenu: boolean = false;
  contextMenuX: number = 0;
  contextMenuY: number = 0;
  selectedTextForContext: string = '';
  selectedHtmlForContext: string = '';
  
  // Smart scroll behavior properties
  private isUserNearBottom: boolean = true;
  private shouldAutoScroll: boolean = true;
  private wheelListener: any;
  private lastScrollTime: number = 0;
  private userScrollingTimeout: any;
  
  // Warning message properties
  showWarning: boolean = false;
  warningMessage: string = '';
  warningTimeout: any;
  
  consultMode: AyaConsultMode = DEFAULT_AYA_CONSULT_MODE;
  consultModeOptions = CONSULT_MODE_OPTIONS;
  showConsultModeMenu = false;
  quickQueryChips: string[] = [];
  streamStatus: string | null = null;
  chatStatus: 'ready' | 'submitted' | 'streaming' | 'error' = 'ready';
  progressPhases: AyaProgressPhase[] = [];
  searchSteps: AyaSearchStep[] = [];
  progressSnapshots: Record<
    string,
    { phases: AyaProgressPhase[]; steps: AyaSearchStep[]; statusLine?: string | null }
  > = {};
  mcpMessageIds = new Set<string>();
  showScrollButton = false;
  editingMessageId: string | null = null;
  editDraft = '';
  contextMenuMessage: ChatMessage | null = null;
  private messageBlocksCache = new Map<string, AyaMessageBlocks>();
  private messageContentVersion = new Map<string, string>();
  private assistantHtmlCache = new Map<string, SafeHtml>();
  private scrollScheduled = false;
  private lastKnownScrollHeight = 0;
  private sawSearch = false;
  private writingStarted = false;
  private lastUserText = '';
  private loadingConversationId: string | null = null;
  private conversationLoadSeq = 0;

  private streamAbort: AbortController | null = null;
  private activeRunId: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private applicationContextService: ApplicationContextService,
    private ayaConversationsService: AyaConversationsService,
    private route: ActivatedRoute,
    private sidebarStateService: SidebarStateService,
    private sanitizer: DomSanitizer,
    public router: Router
  ) {}

  get isBusy(): boolean {
    return (
      this.isLoading ||
      this.chatStatus === 'submitted' ||
      this.chatStatus === 'streaming'
    );
  }

  /** Work-style progress accordion (Ask Aya thoughtful / research modes). */
  get showAyaProgress(): boolean {
    return this.consultMode !== 'quick';
  }

  get consultModeLabel(): string {
    return (
      this.consultModeOptions.find((o) => o.value === this.consultMode)?.label ??
      'Thoughtful'
    );
  }

  get greetingName(): string {
    return this.userName ? `Hi ${this.userName}` : 'Meet Aya';
  }

  readonly splashDisclaimer =
    "Health Exam can make mistakes. Workspace data isn't used to train models.";

  readonly chatDisclaimer =
    'Verify citations before acting — evidence is summarized, not adjudicated.';

  ngOnInit(): void {
    this.quickQueryChips = this.pickRandomQueries(4);
    this.loadMessageReactions();
    
    // Subscribe to application context to get current user
    this.applicationContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe(context => {
        if (context?.currentlyLoggedInUser) {
          this.userName = `${context.currentlyLoggedInUser.firstName}`.trim();
        }
      });
    this.route.queryParams
      .pipe(
        map((params) => ({
          showAll: params['showAll'] === 'true',
          conversationId: String(params['conversationId'] ?? '').trim(),
        })),
        distinctUntilChanged(
          (prev, next) =>
            prev.showAll === next.showAll &&
            prev.conversationId === next.conversationId
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ showAll, conversationId }) => {
        if (showAll) {
          this.currentConversationId = null;
          this.allConversations = true;
          this.showWelcome = false;
          this.messages = [];
          this.syncChatListItems();
          return;
        }
        if (conversationId && conversationId !== 'all') {
          this.allConversations = false;
          this.loadConversation(conversationId);
        }
      });
  }

  ngAfterViewInit(): void {
    // Add wheel event listener to chat container for immediate scroll detection
    if (this.chatContainer && this.chatContainer.nativeElement) {
      this.wheelListener = () => {
        this.updateScrollState();
      };
      this.chatContainer.nativeElement.addEventListener('wheel', this.wheelListener, { passive: true });
    }
  }

  // Detect when user manually scrolls
  onChatScroll(): void {
    if (!this.chatContainer) return;
    this.updateScrollState();
    this.updateShowScrollButton();
  }

  private updateScrollState(): void {
    if (!this.chatContainer) return;
    
    const element = this.chatContainer.nativeElement;
    const threshold = 50; // pixels from bottom to consider "near bottom" (reduced for more precise control)
    const position = element.scrollTop + element.clientHeight;
    const height = element.scrollHeight;
    
    // Calculate distance from bottom
    const distanceFromBottom = height - position;
    
    // User is at bottom if within threshold
    this.isUserNearBottom = distanceFromBottom <= threshold;
    
    // Immediately disable auto-scroll if user scrolls up
    if (!this.isUserNearBottom) {
      this.shouldAutoScroll = false;
    } else {
      // Re-enable if back at bottom (but wait a bit to ensure user finished scrolling)
      clearTimeout(this.userScrollingTimeout);
      this.userScrollingTimeout = setTimeout(() => {
        this.shouldAutoScroll = true;
      }, 100); // Small delay to prevent fighting with user scroll
    }
    
    this.lastScrollTime = Date.now();
  }

  scrollToBottom(smooth = false): void {
    try {
      if (this.chatContainer?.nativeElement) {
        const el = this.chatContainer.nativeElement;
        el.scrollTo({
          top: el.scrollHeight,
          behavior: smooth ? 'smooth' : 'auto',
        });
        this.lastKnownScrollHeight = el.scrollHeight;
      }
    } catch {
      /* ignore */
    }
  }

  private scheduleAutoScroll(): void {
    if (!this.shouldAutoScroll || this.scrollScheduled) {
      return;
    }
    this.scrollScheduled = true;
    requestAnimationFrame(() => {
      this.scrollScheduled = false;
      if (!this.shouldAutoScroll || !this.chatContainer?.nativeElement) {
        return;
      }
      const el = this.chatContainer.nativeElement;
      const height = el.scrollHeight;
      const atBottom = el.scrollTop + el.clientHeight >= height - 4;
      if (atBottom && height === this.lastKnownScrollHeight) {
        return;
      }
      this.lastKnownScrollHeight = height;
      el.scrollTop = height;
    });
  }

  private syncChatListItems(): void {
    this.chatListItems = withDayHeaders(this.messages);
  }

  trackChatItem(_index: number, item: ChatListItem): string {
    return item.type === 'header'
      ? `header-${item.key}`
      : `msg-${item.message.id ?? _index}`;
  }

  private updateShowScrollButton(): void {
    if (!this.chatContainer?.nativeElement) {
      if (this.showScrollButton) {
        this.showScrollButton = false;
      }
      return;
    }
    const el = this.chatContainer.nativeElement;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const next = distance > 120;
    if (next !== this.showScrollButton) {
      this.showScrollButton = next;
    }
  }

  ngOnDestroy(): void {
    this.stopStreaming();
    // Clean up wheel event listener
    if (this.chatContainer && this.chatContainer.nativeElement && this.wheelListener) {
      this.chatContainer.nativeElement.removeEventListener('wheel', this.wheelListener);
    }
    
    // Clean up timeouts
    if (this.userScrollingTimeout) {
      clearTimeout(this.userScrollingTimeout);
    }
    if (this.copyToastTimeout) {
      clearTimeout(this.copyToastTimeout);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    
    // Remove context menu click listener
    document.removeEventListener('click', this.closeContextMenu.bind(this));
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  async sendMessage(textOverride?: string): Promise<void> {
    const raw = (textOverride ?? this.userMessage).trim();
    if (!raw || this.isBusy) {
      return;
    }

    this.dismissWarning();
    this.userMessage = '';
    this.resetTextareaHeight();
    await this.sendAyaMessage(raw);
  }

  useQuickQuery(query: string): void {
    if (this.isBusy) {
      return;
    }
    void this.sendMessage(query);
  }

  toggleConsultModeMenu(event: Event): void {
    event.stopPropagation();
    this.showConsultModeMenu = !this.showConsultModeMenu;
  }

  setConsultMode(mode: AyaConsultMode, event?: Event): void {
    event?.stopPropagation();
    this.consultMode = mode;
    this.showConsultModeMenu = false;
  }

  stopStreaming(): void {
    this.streamAbort?.abort();
    this.streamAbort = null;
    if (this.activeRunId) {
      void this.ayaConversationsService.stopRun(this.activeRunId).catch(() => undefined);
    }
    this.activeRunId = null;
    this.clearProgress();
    this.isTyping = false;
    this.isLoading = false;
    if (this.chatStatus === 'streaming' || this.chatStatus === 'submitted') {
      this.chatStatus = 'ready';
    }
  }

  private clearProgress(): void {
    this.streamStatus = null;
    this.progressPhases = [];
    this.searchSteps = [];
    this.sawSearch = false;
    this.writingStarted = false;
  }

  private pickRandomQueries(count: number): string[] {
    const pool = [...CLINICIAN_QUERY_POOL];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, count);
  }

  private titleFrom(text: string): string {
    const clean = text.replace(/\s+/g, ' ').trim();
    if (!clean) {
      return 'New chat';
    }
    return clean.length > 60 ? `${clean.slice(0, 57)}…` : clean;
  }

  private resetTextareaHeight(): void {
    setTimeout(() => {
      const textarea = document.querySelector('.chat-input') as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = 'auto';
      }
    }, 0);
  }

  private async sendAyaMessage(text: string): Promise<void> {
    const lane = await this.ayaConversationsService.getWorkLane();
    if (!lane) {
      this.showWarningBanner('Sign in required to chat with Aya.');
      return;
    }

    this.showWelcome = false;
    this.allConversations = false;
    this.shouldAutoScroll = true;
    this.chatStatus = 'submitted';
    this.isLoading = true;
    this.clearProgress();
    if (this.showAyaProgress) {
      this.progressPhases = advanceAyaPhases(initialAyaPhases(), 'analyzed');
    }
    this.lastUserText = text;

    const optimisticUserId = this.generateId();
    const userMsg: ChatMessage = {
      id: optimisticUserId,
      content: text,
      isUser: true,
      timestamp: new Date(),
      conversationId: this.currentConversationId ?? '',
    };
    this.messages.push(userMsg);
    this.syncChatListItems();
    this.scheduleAutoScroll();
    this.clearAttachments();

    try {
      let conversationId = this.currentConversationId;
      if (!conversationId || conversationId === 'all') {
        const created = await this.ayaConversationsService.createConversation(
          this.titleFrom(text)
        );
        conversationId = created.id;
        this.currentConversationId = created.id;
        this.currentConversationTitle = created.title?.trim() || this.titleFrom(text);
        this.currentConversationPeriod = 'Today';
        userMsg.conversationId = conversationId;
        void this.ayaConversationsService.refresh();
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { conversationId, showAll: null },
          replaceUrl: true,
        });
      }

      await this.runAyaTurn(text, conversationId!, optimisticUserId);
    } catch (error) {
      console.error('[clinic-aya] send failed', error);
      this.chatStatus = 'error';
      this.isLoading = false;
      this.isTyping = false;
      this.streamStatus = null;
      const detail =
        error instanceof Error ? error.message : 'Aya could not respond. Please try again.';
      this.showWarningBanner(detail);
      this.messages = this.messages.filter((m) => m.id !== optimisticUserId);
      this.syncChatListItems();
    }
  }

  private async runAyaTurn(
    text: string,
    conversationId: string,
    optimisticUserId: string
  ): Promise<void> {
    const assistantId = this.generateId();
    const assistantMsg: ChatMessage = {
      id: assistantId,
      content: '',
      isUser: false,
      timestamp: new Date(),
      conversationId,
    };
    this.messages.push(assistantMsg);
    this.syncChatListItems();
    this.typingMessages.set(assistantId, '');
    this.isTyping = true;

    const run = await this.ayaConversationsService.postMessage(
      conversationId,
      text,
      this.consultMode
    );

    if (run.message_id) {
      const userIndex = this.messages.findIndex((m) => m.id === optimisticUserId);
      if (userIndex >= 0) {
        this.messages[userIndex] = {
          ...this.messages[userIndex],
          id: run.message_id,
        };
      }
    }

    await this.streamAssistantResponse(run, assistantId);
  }

  private async streamAssistantResponse(
    run: AyaRunDto,
    assistantId: string
  ): Promise<void> {
    this.chatStatus = 'streaming';
    this.activeRunId = run.run_id;
    this.streamAbort = new AbortController();
    this.sawSearch = false;
    this.writingStarted = false;
    if (this.showAyaProgress && !this.progressPhases.length) {
      this.progressPhases = advanceAyaPhases(initialAyaPhases(), 'analyzed');
    }
    let buffer = '';
    let liveAssistantId = assistantId;

    const writeAssistant = (content: string) => {
      buffer = content;
      if (this.showAyaProgress) {
        this.ensureWritingPhase();
      }
      this.invalidateMessageCaches(liveAssistantId);
      const idx = this.messages.findIndex((m) => m.id === liveAssistantId);
      if (idx >= 0) {
        this.messages[idx].content = buffer;
      }
      this.typingMessages.set(liveAssistantId, buffer);
      this.scheduleAutoScroll();
    };

    const snapshotProgress = () => {
      const finished = advanceAyaPhases(
        this.progressPhases.length ? this.progressPhases : initialAyaPhases(),
        'done'
      ).map((p) => (p.state === 'active' ? { ...p, state: 'done' as const } : p));
      const doneSteps = this.searchSteps.map((s) => ({ ...s, done: true }));
      const statusLine = this.streamStatus;
      this.progressSnapshots = {
        ...this.progressSnapshots,
        [liveAssistantId]: { phases: finished, steps: doneSteps, statusLine },
      };
      this.progressPhases = finished;
      this.searchSteps = doneSteps;
    };

    try {
      await this.ayaConversationsService.streamRun(
        run,
        {
          onDelta: (delta) => writeAssistant(buffer + delta),
          onMessage: (content) => writeAssistant(content),
          onStatus: (line) => {
            if (this.showAyaProgress) {
              this.applyStreamStatus(line);
            }
          },
          onPhase: ({ phase, label }) => {
            if (this.showAyaProgress) {
              this.handlePhaseEvent(phase, label);
            }
          },
          onSearch: ({ state, query, label }) => {
            if (this.showAyaProgress) {
              this.handleSearchEvent(state, query, label);
            }
          },
          onToolCall: ({ name }) => {
            const n = (name || '').toLowerCase();
            if (!n || n === 'web_search' || n.includes('web_search')) return;
            this.mcpMessageIds.add(liveAssistantId);
          },
          onError: (err) => {
            throw err;
          },
          onDone: (info) => {
            if (
              info.assistantMessageId &&
              info.assistantMessageId !== liveAssistantId
            ) {
              const fromId = liveAssistantId;
              liveAssistantId = info.assistantMessageId;
              this.remapProgressSnapshot(fromId, liveAssistantId);
              if (this.mcpMessageIds.has(fromId)) {
                this.mcpMessageIds.delete(fromId);
                this.mcpMessageIds.add(liveAssistantId);
              }
              const idx = this.messages.findIndex((m) => m.id === fromId);
              if (idx >= 0) {
                this.messages[idx] = {
                  ...this.messages[idx],
                  id: liveAssistantId,
                };
                this.typingMessages.delete(fromId);
                this.typingMessages.set(liveAssistantId, buffer);
              }
            }
            snapshotProgress();
          },
        },
        this.streamAbort.signal
      );

      this.chatStatus = 'ready';
      this.isLoading = false;
      this.isTyping = false;
      this.streamStatus = null;
      this.activeRunId = null;
      this.streamAbort = null;
      void this.ayaConversationsService.refresh();
      this.ayaConversationsService
        .loadMessages(run.conversation_id, true)
        .catch(() => undefined);
    } catch (error) {
      if (this.streamAbort?.signal.aborted) {
        return;
      }
      throw error;
    }
  }

  private handlePhaseEvent(phase: string, label?: string): void {
    if (!this.showAyaProgress) return;
    const raw = `${phase} ${label ?? ''}`;
    const phaseKey = mapPhaseKey(phase) ?? (label ? mapPhaseKey(label) : null);

    if (
      /skip/i.test(raw) &&
      (/search|research|literat/i.test(raw) || phaseKey === 'searching')
    ) {
      this.sawSearch = false;
      this.searchSteps = [];
      this.streamStatus = null;
      this.progressPhases = advanceAyaPhases(
        this.progressPhases.length ? this.progressPhases : initialAyaPhases(),
        'searching',
        { skipped: true, label: 'Skipped literature search' }
      );
      this.ensureWritingPhase();
      return;
    }

    if (phaseKey === 'searching') {
      if (label) this.applyStreamStatus(label);
      return;
    }

    if (phaseKey === 'writing') {
      this.ensureWritingPhase();
      return;
    }

    if (phaseKey === 'analyzed' || phaseKey === 'done') {
      this.progressPhases = advanceAyaPhases(
        this.progressPhases.length ? this.progressPhases : initialAyaPhases(),
        phaseKey,
        { label }
      );
      if (phaseKey === 'analyzed') {
        this.progressPhases = this.progressPhases.map((p) =>
          p.key === 'analyzed' ? { ...p, state: 'done' as const } : p
        );
      }
      return;
    }

    if (label) this.applyStreamStatus(label);
  }

  private applyStreamStatus(line: string | null | undefined): void {
    const t = line?.trim();
    if (!t || isRunLifecycleStatus(t)) return;
    this.streamStatus = t;
  }

  private handleSearchEvent(
    state: string,
    query?: string,
    serverLabel?: string
  ): void {
    if (!this.showAyaProgress) return;
    this.sawSearch = true;
    this.progressPhases = advanceAyaPhases(
      this.progressPhases.length ? this.progressPhases : initialAyaPhases(),
      'searching',
      { label: 'Searching literature' }
    );
    const label =
      serverLabel ??
      (query
        ? `Searching: ${query}`
        : state === 'started'
          ? 'Searching the literature…'
          : null);
    if (label) this.applyStreamStatus(label);

    const line = query?.trim() || serverLabel?.trim() || label;
    if (!line) return;
    if (this.searchSteps.some((s) => s.label === line || s.query === query)) return;
    this.searchSteps = [
      ...this.searchSteps.map((s) => ({ ...s, done: true })),
      {
        id: `${this.searchSteps.length}-${line}`,
        label: line,
        query: query?.trim() || undefined,
        done: false,
      },
    ];
  }

  private ensureWritingPhase(): void {
    if (this.writingStarted) return;
    this.writingStarted = true;
    const base = this.progressPhases.length ? this.progressPhases : initialAyaPhases();
    this.progressPhases = this.sawSearch
      ? advanceAyaPhases(base, 'writing')
      : advanceAyaPhases(
          advanceAyaPhases(base, 'searching', {
            skipped: true,
            label: 'Skipped literature search',
          }),
          'writing'
        );
  }

  private remapProgressSnapshot(fromId: string, toId: string): void {
    if (fromId === toId || !(fromId in this.progressSnapshots)) return;
    const next = { ...this.progressSnapshots };
    next[toId] = next[fromId];
    delete next[fromId];
    this.progressSnapshots = next;
    this.invalidateMessageCaches(fromId);
    this.invalidateMessageCaches(toId);
  }

  private invalidateMessageCaches(messageId: string): void {
    this.messageBlocksCache.delete(messageId);
    this.messageContentVersion.delete(messageId);
    for (const key of [...this.assistantHtmlCache.keys()]) {
      if (key.startsWith(`${messageId}:`)) {
        this.assistantHtmlCache.delete(key);
      }
    }
  }

  /**
   * Shows a warning banner at the top of the chat
   */
  private showWarningBanner(message: string): void {
    this.warningMessage = message;
    this.showWarning = true;
    
    // Clear any existing timeout
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    
    // Auto-hide warning after 8 seconds
    this.warningTimeout = setTimeout(() => {
      this.showWarning = false;
    }, 8000);
  }

  /**
   * Manually dismiss the warning banner
   */
  dismissWarning(): void {
    this.showWarning = false;
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
  }

  openAllConversationsSearch(): void {
    void this.router.navigate(['/chats'], {
      queryParams: {
        conversationId: 'all',
        showAll: 'true',
      },
    });
  }

  startNewConversation() {
    this.stopStreaming();
    this.messages = [];
    this.syncChatListItems();
    this.showWelcome = true;
    this.allConversations = false;
    this.userMessage = '';
    this.currentConversationId = null;
    this.currentConversationPeriod = 'Today';
    this.currentConversationTitle = '';
    this.shouldAutoScroll = true;
    this.chatStatus = 'ready';
    this.progressSnapshots = {};
    this.mcpMessageIds.clear();
    this.messageBlocksCache.clear();
    this.assistantHtmlCache.clear();
    this.editingMessageId = null;
    this.editDraft = '';
    this.quickQueryChips = this.pickRandomQueries(4);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { conversationId: null, showAll: null },
      replaceUrl: true,
    });
  }

  loadConversation(conversationId: string) {
    void this.loadConversationAsync(conversationId);
  }

  private async loadConversationAsync(conversationId: string): Promise<void> {
    if (this.isBusy) {
      return;
    }
    if (
      this.currentConversationId === conversationId &&
      this.messages.length > 0 &&
      !this.showWelcome
    ) {
      return;
    }
    if (this.loadingConversationId === conversationId) {
      return;
    }
    this.loadingConversationId = conversationId;
    const loadSeq = ++this.conversationLoadSeq;

    try {
      let ayaConversation =
        this.ayaConversationsService.getConversation(conversationId);

      if (!ayaConversation) {
        await this.ayaConversationsService.refresh();
        ayaConversation =
          this.ayaConversationsService.getConversation(conversationId);
      }

      if (!ayaConversation) {
        ayaConversation =
          (await this.ayaConversationsService.fetchConversation(conversationId)) ??
          undefined;
      }

      if (!ayaConversation) {
        console.warn('[clinic-aya] Conversation not found', conversationId);
        return;
      }

      const cachedMessages = await this.ayaConversationsService.loadMessages(
        conversationId
      );
      if (loadSeq !== this.conversationLoadSeq) {
        return;
      }
      this.applyLoadedConversation(ayaConversation, cachedMessages);
    } catch (err) {
      console.warn('[clinic-aya] Failed to load conversation', err);
    } finally {
      if (this.loadingConversationId === conversationId) {
        this.loadingConversationId = null;
      }
    }
  }

  private applyLoadedConversation(
    conversation: ChatConversation,
    messages: ChatMessage[]
  ): void {
    this.currentConversationId = conversation.id || null;
    this.allConversations = false;
    this.messages = [...messages];
    this.syncChatListItems();
    this.showWelcome = false;
    this.messageBlocksCache.clear();
    this.messageContentVersion.clear();
    this.assistantHtmlCache.clear();
    this.currentConversationPeriod = this.getConversationPeriod(
      conversation.timestamp
    );
    this.currentConversationTitle = conversation.title;
    messages.forEach((msg) => {
      if (!msg.isUser && msg.id) {
        this.typingMessages.set(msg.id, msg.content);
      }
    });
    this.shouldAutoScroll = true;
    this.scheduleAutoScroll();
  }



  onConversationSelected(conversationId: string) {
    if (conversationId === 'all') {
      // Don't set a conversation ID when showing all conversations
      this.currentConversationId = null;
      this.allConversations = false;
      this.showWelcome = false;
      this.messages = [];
      this.syncChatListItems();
    } else {
      // Load the specific conversation from history
      this.loadConversation(conversationId);
      this.allConversations = false;
    }
  }

  // Dropdown and file upload methods
  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  closeDropdown() {
    this.showDropdown = false;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      
      // Add new files to attachments
      files.forEach(file => {
        if (this.isValidFile(file)) {
          this.attachments.push(file);
          this.createPreview(file);
        }
      });
      
      this.closeDropdown();
    }
  }

  private isValidFile(file: File): boolean {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
    
    if (file.size > maxSize) {
      alert('File size must be less than 10MB');
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      alert('File type not supported. Please upload images (JPEG, PNG, GIF, WebP), PDF, or text files.');
      return false;
    }
    
    return true;
  }

  private createPreview(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.attachmentPreviews.push(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  removeAttachment(index: number) {
    this.attachments.splice(index, 1);
    this.attachmentPreviews.splice(index, 1);
  }

  clearAttachments() {
    this.attachments = [];
    this.attachmentPreviews = [];
  }

  onEnterKey(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  adjustTextareaHeight() {
    const textarea = document.querySelector('.chat-input') as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 280) + 'px';
    }
  }

  private getConversationPeriod(timestamp: Date): string {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    if (timestamp >= today) {
      return 'Today';
    } else if (timestamp >= yesterday) {
      return 'Yesterday';
    } else if (timestamp >= lastWeek) {
      return 'Last Week';
    } else if (timestamp >= lastMonth) {
      return 'Last Month';
    } else {
      return timestamp.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
    }
  }

  processMedicalSections(content: string): string {
    let processed = content;
    
    // Process Summary section with table
    processed = processed.replace(/^(#+)?\s*Summary:?\s*$/gim, () => {
      return '<div class="medical-summary"><h3 class="summary-heading"><span class="summary-icon">✅</span>Summary</h3>';
    });
    
    // Process symptom-description table format
    // Look for patterns like "Symptom: Description" or "- Symptom: Description"
    processed = processed.replace(/^[-*]\s*([^:]+):\s*(.+)$/gim, (match, symptom, description) => {
      if (processed.includes('Summary') || processed.includes('SUMMARY')) {
        return `<div class="summary-table-row"><div class="symptom-col">${symptom.trim()}</div><div class="description-col">${description.trim()}</div></div>`;
      }
      return match;
    });
    
    // Process "When to See a Doctor" section
    processed = processed.replace(/^(#+)?\s*When to See (a|the) Doctor:?\s*$/gim, () => {
      return '</div><div class="when-to-see-doctor"><h3 class="doctor-heading"><span class="doctor-icon">👨‍⚕️</span>When to See a Doctor</h3>';
    });
    
    // Process Sources section
    processed = processed.replace(/^(#+)?\s*Sources?:?\s*$/gim, () => {
      return '</div><div class="sources-section"><h3 class="sources-heading"><span class="sources-icon">🔗</span>Sources</h3><ul class="sources-list">';
    });
    
    // Convert URLs to clickable links in sources
    processed = processed.replace(/^[-*]\s*(https?:\/\/[^\s]+)\s*$/gim, (match, url) => {
      return `<li class="source-item"><a href="${url}" target="_blank" rel="noopener noreferrer" class="source-link">${url}</a></li>`;
    });
    
    // Close any open divs
    if (processed.includes('sources-section')) {
      processed += '</ul></div>';
    } else if (processed.includes('when-to-see-doctor')) {
      processed += '</div>';
    } else if (processed.includes('medical-summary')) {
      processed += '</div>';
    }
    
    return processed;
  }

  formatMessage(content: string): string {
    // Enhanced formatting for Microsoft Copilot-style responses
    let formatted = content;
    
    // Detect structured content patterns (like "Common Symptoms of Fever")
    const isStructuredContent = this.detectStructuredContent(formatted);
    
    // Process special medical sections (Summary, When to See a Doctor, Sources)
    formatted = this.processMedicalSections(formatted);
    
    // Process headers with medical icons first (before other replacements)
    const processHeader = (match: string, level: string, heading: string): string => {
      const icon = this.addMedicalIcon(heading);
      return `<h${level} class="content-heading">${icon}${heading}</h${level}>`;
    };
    
    // Convert plain text headings to HTML headers
    // Look for lines that look like headings (e.g., "Primary Symptom", "Character of the Pain", etc.)
    formatted = formatted.replace(/^([A-Z][A-Za-z\s]+(?:Symptom|Pain|Character|Treatment|Causes|Diagnosis|Prevention|Complications|Risk Factors|Signs|Effects|Conditions|Features|Aspects|Location|Duration|Intensity|Triggers|Relief|Warning|When to See|Additional|Common|Rare|Serious)[^:.\n]*)[:\n]/gim, (match, heading) => {
      const icon = this.addMedicalIcon(heading.trim());
      return `<h2 class="content-heading">${icon}${heading.trim()}</h2>\n`;
    });
    
    // Process markdown headers
    formatted = formatted
      .replace(/^### (.*)$/gim, (match, heading) => processHeader(match, '3', heading))
      .replace(/^## (.*)$/gim, (match, heading) => processHeader(match, '2', heading))
      .replace(/^# (.*)$/gim, (match, heading) => processHeader(match, '1', heading));
    
    // Convert markdown-style formatting to HTML
    formatted = formatted
      // Bold text with medical icons
      .replace(/\*\*(.*?)\*\*/g, (match, text) => {
        const icon = this.addMedicalIcon(text);
        if (icon) {
          return `<strong class="content-bold">${text}</strong>`;
        }
        return `<strong class="content-bold">${text}</strong>`;
      })
      .replace(/\*(.*?)\*/g, '<em class="content-italic">$1</em>')
      
      // Source tags - handle [source +1] format
      .replace(/\[([^\]]+)\s+\+(\d+)\]/g, '<span class="source-tag">$1 +$2</span>')
      
      // Lists - handle both numbered and bulleted lists with better structure
      .replace(/^(\d+)\.\s+(.*$)/gim, (match, number, text) => {
        // Don't show items with placeholder variables
        if (text.includes('$1') || text.includes('$2') || !text.trim()) {
          return '';
        }
        return `<div class="content-list-item d-flex"><span class="list-number">${number}.</span><span class="list-text ps-1">${text}</span></div>`;
      })
      .replace(/^[-*]\s+(.*$)/gim, (match, text) => {
        // Don't show items with placeholder variables
        if (text.includes('$1') || text.includes('$2') || !text.trim()) {
          return '';
        }
        return `<div class="content-list-item ps-3 d-flex"><span class="list-bullet">•</span><span class="list-text ps-1">${text}</span></div>`;
      })
      
      // Nested lists - handle sub-items
      .replace(/^(\s{2,})[-*]\s+(.*$)/gim, (match, spaces, text) => {
        // Don't show items with placeholder variables
        if (text.includes('$1') || text.includes('$2') || !text.trim()) {
          return '';
        }
        return `<div class="content-list-item nested ps-4 mb-1 d-flex"><span class="list-bullet">•</span><span class="list-text ps-3">${text}</span></div>`;
      })
      
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre class="content-code-block"><code>$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="content-inline-code">$1</code>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="content-link" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Handle line breaks and paragraphs
    formatted = formatted
      .replace(/\n\n/g, '</p><p class="content-paragraph mt-3 mb-1">')
      .replace(/\n/g, '');
    
    // Wrap in structured container if detected
    if (isStructuredContent) {
      formatted = `<div class="content-structured">${formatted}</div>`;
    }
    
    // Simple paragraph wrapping - only wrap if no HTML tags are present
    if (!formatted.includes('<') && formatted.trim()) {
      formatted = `<p class="content-paragraph">${formatted}</p>`;
    }
    
    // Clean up empty paragraphs
    formatted = formatted.replace(/<p class="content-paragraph"><\/p>/g, '');
    
    return formatted;
  }

  private addMedicalIcon(heading: string): string {
    const lowerHeading = heading.toLowerCase();
    
    // Medical condition and symptom icons mapping
    const medicalIcons: { [key: string]: string } = {
      // Primary categories from the image
      'primary symptom': '🧠',
      'primary symptoms': '🧠',
      'main symptom': '🧠',
      'main symptoms': '🧠',
      'character of the pain': '⚡',
      'pain character': '⚡',
      'pain characteristics': '⚡',
      'type of pain': '⚡',
      'pain type': '⚡',
      
      // Specific symptoms
      'fever': '🌡️',
      'temperature': '🌡️',
      'chills': '🥶',
      'shivering': '🥶',
      'shaking': '🥶',
      'cold sweats': '🥶',
      'sweating': '💦',
      'perspiration': '💦',
      'night sweats': '💦',
      'hot flashes': '🔥',
      'heat': '🔥',
      'heat exhaustion': '🥵',
      'overheating': '🥵',
      'headache': '🤕',
      'migraine': '🤕',
      'head pain': '🤕',
      'pain': '😣',
      'ache': '😣',
      'sore': '😣',
      'discomfort': '😣',
      'cough': '🤧',
      'coughing': '🤧',
      'cold': '🤧',
      'flu': '🤧',
      'influenza': '🤧',
      'nausea': '🤢',
      'vomit': '🤮',
      'vomiting': '🤮',
      'throwing up': '🤮',
      'diarrhea': '💩',
      'constipation': '💩',
      'bowel': '💩',
      'rash': '🔴',
      'skin': '🔴',
      'itching': '🔴',
      'itch': '🔴',
      'hives': '🔴',
      'allergy': '🤧',
      'allergic': '🤧',
      'asthma': '🫁',
      'breathing': '🫁',
      'respiratory': '🫁',
      'breathlessness': '🫁',
      'shortness of breath': '🫁',
      'wheezing': '🫁',
      'chest': '🫁',
      'heart': '❤️',
      'cardiac': '❤️',
      'palpitations': '❤️',
      'blood': '🩸',
      'bleeding': '🩸',
      'pressure': '📊',
      'hypertension': '📊',
      'hypotension': '📊',
      'diabetes': '🍯',
      'sugar': '🍯',
      'insulin': '💉',
      'injection': '💉',
      'medicine': '💊',
      'medication': '💊',
      'pill': '💊',
      'tablet': '💊',
      'capsule': '💊',
      'dose': '💊',
      'treatment': '🏥',
      'therapy': '🏥',
      'hospital': '🏥',
      'clinic': '🏥',
      'doctor': '👨‍⚕️',
      'nurse': '👩‍⚕️',
      'patient': '🏥',
      'emergency': '🚨',
      'urgent': '🚨',
      'symptoms': '🔍',
      'signs': '🔍',
      'diagnosis': '🔬',
      'test': '🔬',
      'lab': '🔬',
      'x-ray': '📷',
      'scan': '📷',
      'mri': '📷',
      'ct': '📷',
      'ultrasound': '📷',
      'surgery': '⚕️',
      'operation': '⚕️',
      'wound': '🩹',
      'injury': '🩹',
      'cut': '🩹',
      'burn': '🔥',
      'fracture': '🦴',
      'bone': '🦴',
      'muscle': '💪',
      'joint': '🦴',
      'arthritis': '🦴',
      'inflammation': '🔥',
      'infection': '🦠',
      'bacteria': '🦠',
      'virus': '🦠',
      'immune': '🛡️',
      'vaccine': '💉',
      'Vaccinations': '💉',
      'immunization': '💉',
      'prevention': '🛡️',
      'exercise': '🏃',
      'fitness': '💪',
      'diet': '🥗',
      'nutrition': '🥗',
      'vitamin': '💊',
      'supplement': '💊',
      'weakness': '😮‍💨',
      'weak': '😮‍💨',
      'fatigue': '😴',
      'tired': '😴',
      'exhaustion': '😴',
      'tiredness': '😴',
      'lethargy': '😴',
      'drowsiness': '😴',
      'sleep': '😴',
      'insomnia': '😴',
      'sleeplessness': '😴',
      'appetite': '🍽️',
      'hunger': '🍽️',
      'loss of appetite': '🍽️',
      'anorexia': '🍽️',
      'eating': '🍽️',
      'dehydration': '💧',
      'thirsty': '💧',
      'thirst': '💧',
      'dry mouth': '💧',
      'dryness': '💧',
      'malnutrition': '🥗',
      'underweight': '⚖️',
      'weight loss': '⚖️',
      'weight gain': '⚖️',
      'obesity': '⚖️',
      'stress': '😰',
      'stressed': '😰',
      'tension': '😰',
      'anxiety': '😰',
      'anxious': '😰',
      'panic': '😰',
      'worry': '😰',
      'depression': '😔',
      'depressed': '😔',
      'sadness': '😔',
      'mood': '😔',
      'mental': '🧠',
      'brain': '🧠',
      'neurological': '🧠',
      'cognitive': '🧠',
      'memory': '🧠',
      'confusion': '🧠',
      'disorientation': '🧠',
      'dizziness': '🌀',
      'dizzy': '🌀',
      'vertigo': '🌀',
      'lightheaded': '🌀',
      'faint': '🌀',
      'fainting': '🌀',
      'syncope': '🌀',
      'numbness': '🔌',
      'numb': '🔌',
      'tingling': '⚡',
      'pins and needles': '⚡',
      'tremor': '📳',
      'trembling': '📳',
      'shakes': '📳',
      'seizure': '⚡',
      'convulsion': '⚡',
      'spasm': '⚡',
      'cramp': '⚡',
      'stiffness': '🥶',
      'rigidity': '🥶',
      'paralysis': '❌',
      'immobility': '❌',
      'swelling': '💢',
      'swollen': '💢',
      'edema': '💢',
      'bloating': '💢',
      'distension': '💢',
      'eye': '👁️',
      'vision': '👁️',
      'blurred vision': '👁️',
      'blind': '👁️',
      'ear': '👂',
      'hearing': '👂',
      'earache': '👂',
      'tinnitus': '👂',
      'ringing': '👂',
      'nose': '👃',
      'nasal': '👃',
      'congestion': '👃',
      'runny nose': '👃',
      'sneezing': '🤧',
      'throat': '👄',
      'sore throat': '👄',
      'hoarse': '👄',
      'mouth': '👄',
      'teeth': '🦷',
      'dental': '🦷',
      'toothache': '🦷',
      'gum': '🦷',
      'abdominal': '🫄',
      'abdomen': '🫄',
      'stomach pain': '🫄',
      'belly': '🫄',
      'indigestion': '🤢',
      'heartburn': '🔥',
      'acid reflux': '🔥',
      'gas': '💨',
      'flatulence': '💨',
      'burping': '💨',
      'belching': '💨',
      'urinary': '🚽',
      'urine': '🚽',
      'urination': '🚽',
      'bladder': '🚽',
      'kidney': '🫘',
      'renal': '🫘',
      'incontinence': '🚽',
      'frequent urination': '🚽',
      'back pain': '🦴',
      'lower back': '🦴',
      'neck pain': '🦴',
      'shoulder': '💪',
      'arm': '💪',
      'leg': '🦵',
      'foot': '🦶',
      'ankle': '🦶',
      'knee': '🦴',
      'hip': '🦴',
      'wrist': '💪',
      'hand': '🤚',
      'finger': '👆',
      'toe': '🦶',
      'pregnancy': '🤰',
      'pregnant': '🤰',
      'prenatal': '🤰',
      'postnatal': '👶',
      'baby': '👶',
      'infant': '👶',
      'newborn': '👶',
      'child': '👶',
      'children': '👶',
      'pediatric': '👶',
      'toddler': '👶',
      'adolescent': '🧒',
      'teenager': '🧒',
      'elderly': '👴',
      'senior': '👴',
      'geriatric': '👴',
      'age': '👴',
      'aging': '👴',
      
      // Drugs and Substances
      'cigarette': '🚬',
      'smoking': '🚬',
      'smoke': '🚬',
      'tobacco': '🚬',
      'nicotine': '🚬',
      'vaping': '💨',
      'vape': '💨',
      'e-cigarette': '💨',
      'alcohol': '🍺',
      'alcoholic': '🍺',
      'drinking': '🍺',
      'liquor': '🍺',
      'beer': '🍺',
      'wine': '🍷',
      'drunk': '🍺',
      'intoxication': '🍺',
      'drug': '💊',
      'drugs': '💊',
      'substance abuse': '💊',
      'addiction': '⚠️',
      'addicted': '⚠️',
      'dependency': '⚠️',
      'withdrawal': '⚠️',
      'marijuana': '🌿',
      'cannabis': '🌿',
      'weed': '🌿',
      'cocaine': '⚠️',
      'heroin': '💉',
      'opioid': '💊',
      'opioids': '💊',
      'morphine': '💊',
      'fentanyl': '💊',
      'methamphetamine': '⚠️',
      'meth': '⚠️',
      'amphetamine': '💊',
      'stimulant': '⚡',
      'stimulants': '⚡',
      'depressant': '😴',
      'depressants': '😴',
      'sedative': '😴',
      'tranquilizer': '😴',
      'benzodiazepine': '💊',
      'xanax': '💊',
      'valium': '💊',
      'overdose': '🚨',
      'intoxicated': '🍺',
      'substance': '💊',
      'narcotic': '💊',
      'narcotics': '💊',
      'hallucinogen': '🌀',
      'psychedelic': '🌀',
      'caffeine': '☕',
      'coffee': '☕',
      'energy drink': '⚡',
      'steroid': '💪',
      'steroids': '💪',
      'anabolic': '💪'
    };
    
    // Find matching medical term
    for (const [term, icon] of Object.entries(medicalIcons)) {
      if (lowerHeading.includes(term)) {
        return `<span class="medical-icon"></span>`;
      }
    }
    
    // Default medical icon for health-related headings
    if (this.isHealthRelated(heading)) {
      return '<span class="medical-icon"></span>';
    }
    
    return '';
  }

  private isHealthRelated(heading: string): boolean {
    const healthKeywords = [
      'health', 'medical', 'clinical', 'symptom', 'condition', 'disease', 
      'illness', 'disorder', 'syndrome', 'common', 'typical', 'signs',
      'causes', 'risk', 'prevention', 'care', 'management', 'guidelines'
    ];
    
    const lowerHeading = heading.toLowerCase();
    return healthKeywords.some(keyword => lowerHeading.includes(keyword));
  }

  private detectStructuredContent(content: string): boolean {
    // Detect patterns that suggest structured content like lists, headings, or organized information
    const structuredPatterns = [
      /^[A-Z][^.]*:\s*$/m, // Lines ending with colon (like "Common Symptoms of Fever:")
      /^\d+\.\s+/m, // Numbered lists
      /^[-*]\s+/m, // Bulleted lists
      /^#{1,3}\s+/m, // Markdown headers
      /^[A-Z][^.]*Symptoms[^.]*$/m, // Content about symptoms
      /^[A-Z][^.]*Treatment[^.]*$/m, // Content about treatment
      /^[A-Z][^.]*Causes[^.]*$/m, // Content about causes
    ];
    
    return structuredPatterns.some(pattern => pattern.test(content));
  }

  getDisplayedContent(messageId: string, fullContent: string): string {
    if (this.typingMessages.has(messageId)) {
      return this.typingMessages.get(messageId) || '';
    }
    return fullContent;
  }

  getMessageBlocks(message: ChatMessage): AyaMessageBlocks {
    const id = message.id ?? '';
    const content = this.getDisplayedContent(id, message.content);
    const cached = this.messageBlocksCache.get(id);
    if (cached && this.messageContentVersion.get(id) === content) {
      return cached;
    }
    const blocks = splitAyaBlocks(content);
    if (id) {
      this.messageBlocksCache.set(id, blocks);
      this.messageContentVersion.set(id, content);
    }
    return blocks;
  }

  getAssistantBody(message: ChatMessage): string {
    return this.getMessageBlocks(message).body;
  }

  formatAssistantHtml(body: string, messageId: string): SafeHtml {
    const cacheKey = `${messageId}:${body.length}:${body.slice(-24)}`;
    const cached = this.assistantHtmlCache.get(cacheKey);
    if (cached) return cached;
    const html = this.sanitizer.bypassSecurityTrustHtml(
      formatAyaProse(body, messageId)
    );
    this.assistantHtmlCache.set(cacheKey, html);
    return html;
  }

  shouldShowProgress(message: ChatMessage): boolean {
    if (message.isUser || !this.showAyaProgress) return false;
    const snap = message.id ? this.progressSnapshots[message.id] : undefined;
    const phases = snap?.phases ?? (this.isStreamingMessage(message) ? this.progressPhases : []);
    const steps = snap?.steps ?? (this.isStreamingMessage(message) ? this.searchSteps : []);
    const status = this.isStreamingMessage(message) ? this.streamStatus : null;
    return phases.length > 0 || steps.length > 0 || Boolean(status);
  }

  getProgressPhases(message: ChatMessage): AyaProgressPhase[] {
    const snap = message.id ? this.progressSnapshots[message.id] : undefined;
    if (snap?.phases.length) return snap.phases;
    if (this.isStreamingMessage(message)) return this.progressPhases;
    return [];
  }

  getSearchSteps(message: ChatMessage): AyaSearchStep[] {
    const snap = message.id ? this.progressSnapshots[message.id] : undefined;
    if (snap?.steps.length) return snap.steps;
    if (this.isStreamingMessage(message)) return this.searchSteps;
    return [];
  }

  getSearchStatus(message: ChatMessage): string | null {
    if (this.isStreamingMessage(message)) return this.streamStatus;
    const snap = message.id ? this.progressSnapshots[message.id] : undefined;
    return snap?.statusLine ?? null;
  }

  shouldShowAssistantThinking(message: ChatMessage): boolean {
    if (message.isUser || !this.isStreamingMessage(message)) return false;
    if (this.shouldShowProgress(message)) return false;
    return !this.getAssistantBody(message)?.trim();
  }

  shouldShowPendingProgress(): boolean {
    if (!this.showAyaProgress || !this.isBusy || !this.messages.length) return false;
    const hasProgress =
      this.progressPhases.length > 0 ||
      this.searchSteps.length > 0 ||
      Boolean(this.streamStatus);
    if (!hasProgress) return false;
    const last = this.messages[this.messages.length - 1];
    return Boolean(last?.isUser);
  }

  isStreamingMessage(message: ChatMessage): boolean {
    if (!message.id || message.isUser) return false;
    const last = this.messages[this.messages.length - 1];
    return this.isBusy && last?.id === message.id && !last.isUser;
  }

  isLastAssistant(message: ChatMessage): boolean {
    const assistants = this.messages.filter((m) => !m.isUser);
    return assistants.length > 0 && assistants[assistants.length - 1].id === message.id;
  }

  hideAttribution(message: ChatMessage): boolean {
    return Boolean(message.id && this.mcpMessageIds.has(message.id));
  }

  canEditMessage(message: ChatMessage): boolean {
    return (
      message.isUser &&
      Boolean(message.id && isServerMessageId(message.id)) &&
      !this.isBusy &&
      this.editingMessageId !== message.id
    );
  }

  onUserContextMenu(event: MouseEvent, message: ChatMessage): void {
    if (!message.isUser) return;
    this.contextMenuMessage = message;
    this.onMessageContextMenu(event, message);
  }

  beginEditFromMenu(): void {
    if (!this.contextMenuMessage) return;
    this.beginEdit(this.contextMenuMessage);
    this.closeContextMenu();
  }

  beginEdit(message: ChatMessage): void {
    if (!this.canEditMessage(message)) return;
    this.editingMessageId = message.id!;
    this.editDraft = message.content;
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editDraft = '';
  }

  onEditKeydown(event: KeyboardEvent, message: ChatMessage): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.submitEdit(message);
    }
  }

  async submitEdit(message: ChatMessage): Promise<void> {
    const trimmed = this.editDraft.trim();
    if (!trimmed || !message.id) return;
    if (trimmed === message.content.trim()) {
      this.cancelEdit();
      return;
    }
    await this.editUserMessage(message.id, trimmed);
    this.cancelEdit();
  }

  async editUserMessage(messageId: string, text: string): Promise<void> {
    const conversationId = this.currentConversationId;
    if (!conversationId || !isServerMessageId(messageId)) return;

    if (this.isBusy) this.stopStreaming();

    const index = this.messages.findIndex((m) => m.id === messageId);
    if (index < 0) return;

    const keptIds = new Set(this.messages.slice(0, index + 1).map((m) => m.id!));
    this.lastUserText = text;
    this.messages = [
      ...this.messages.slice(0, index + 1).map((m, i) =>
        i === index ? { ...m, content: text } : m
      ),
    ];
    this.syncChatListItems();
    this.progressSnapshots = Object.fromEntries(
      Object.entries(this.progressSnapshots).filter(([id]) => keptIds.has(id))
    );
    this.mcpMessageIds = new Set(
      [...this.mcpMessageIds].filter((id) => keptIds.has(id))
    );

    const assistantId = this.generateId();
    this.messages.push({
      id: assistantId,
      content: '',
      isUser: false,
      timestamp: new Date(),
      conversationId,
    });
    this.syncChatListItems();
    this.chatStatus = 'submitted';
    this.isLoading = true;
    this.clearProgress();
    if (this.showAyaProgress) {
      this.progressPhases = advanceAyaPhases(initialAyaPhases(), 'analyzed');
    }

    try {
      const run = await this.ayaConversationsService.editMessage(messageId, text);
      if (run.message_id) {
        const userIndex = this.messages.findIndex((m) => m.id === messageId);
        if (userIndex >= 0) {
          this.messages[userIndex] = { ...this.messages[userIndex], id: run.message_id };
        }
      }
      await this.streamAssistantResponse(run, assistantId);
    } catch (error) {
      console.error('[clinic-aya] edit failed', error);
      this.chatStatus = 'error';
      this.isLoading = false;
      this.showWarningBanner(
        error instanceof Error ? error.message : 'Could not edit that message.'
      );
    }
  }

  async regenerate(): Promise<void> {
    const lastUser = [...this.messages].reverse().find((m) => m.isUser);
    if (!lastUser?.id) return;
    const text = lastUser.content.trim() || this.lastUserText.trim();
    if (!text) return;

    if (isServerMessageId(lastUser.id)) {
      await this.editUserMessage(lastUser.id, text);
      return;
    }

    const lastAssistant = this.messages[this.messages.length - 1];
    if (lastAssistant && !lastAssistant.isUser) {
      this.messages = this.messages.slice(0, -1);
      this.syncChatListItems();
    }
    await this.sendAyaMessage(text);
  }

  onProseClick(event: MouseEvent, messageId: string): void {
    const target = event.target as HTMLElement;
    const btn = target.closest('.aya-cite-badge') as HTMLElement | null;
    if (!btn) return;
    const index = Number(btn.dataset['sourceIndex']);
    if (Number.isFinite(index)) scrollToSourceCard(messageId, index);
  }

  private async typewriterEffect(messageId: string, fullContent: string): Promise<void> {
    let displayedContent = '';
    
    this.typingMessages.set(messageId, displayedContent);
    
    // Character-by-character typing for smoother effect
    for (let i = 0; i < fullContent.length; i++) {
      displayedContent += fullContent[i];
      this.typingMessages.set(messageId, displayedContent);
      
      // Variable delay for more natural typing rhythm
      let delay = 10; // Base delay of 30ms per character
      
      // Longer pause after punctuation
      if (fullContent[i] === '.' || fullContent[i] === '!' || fullContent[i] === '?') {
        delay = 50;
      } else if (fullContent[i] === ',' || fullContent[i] === ';' || fullContent[i] === ':') {
        delay = 30;
      }
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    // Mark typing as complete for this message
    this.typingMessages.set(messageId, fullContent);
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private generateTitleFromMessage(message: string): string {
    // Generate a title from the first user message
    const words = message.trim().split(' ');
    if (words.length <= 6) {
      return message.trim();
    }
    return words.slice(0, 6).join(' ') + '...';
  }

  /**
   * Builds conversation history from messages for thread-based chat
   * Converts ChatMessage[] to OpenAI message format
   */
  private buildConversationHistory(): Array<{role: string, content: string}> {
    const history: Array<{role: string, content: string}> = [];
    
    // Convert existing messages to OpenAI format
    // Only include actual conversation messages (not attachments or system messages)
    for (const msg of this.messages) {
      history.push({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content
      });
    }
    
    return history;
  }
  get isSidebarExpanded(): boolean {
    return this.sidebarStateService.isSidebarExpanded;
  }
  // Message action methods
  loadMessageReactions(): void {
    try {
      const liked = localStorage.getItem('ai-chat-liked-messages');
      const disliked = localStorage.getItem('ai-chat-disliked-messages');
      
      if (liked) {
        this.likedMessages = new Set(JSON.parse(liked));
      }
      if (disliked) {
        this.dislikedMessages = new Set(JSON.parse(disliked));
      }
    } catch (error) {
      console.error('Error loading message reactions:', error);
    }
  }

  saveMessageReactions(): void {
    try {
      localStorage.setItem('ai-chat-liked-messages', JSON.stringify([...this.likedMessages]));
      localStorage.setItem('ai-chat-disliked-messages', JSON.stringify([...this.dislikedMessages]));
    } catch (error) {
      console.error('Error saving message reactions:', error);
    }
  }

  isMessageLiked(messageId: string): boolean {
    return this.likedMessages.has(messageId);
  }

  isMessageDisliked(messageId: string): boolean {
    return this.dislikedMessages.has(messageId);
  }

  toggleLike(messageId: string): void {
    if (this.likedMessages.has(messageId)) {
      this.likedMessages.delete(messageId);
    } else {
      // Remove from disliked if it was there
      this.dislikedMessages.delete(messageId);
      this.likedMessages.add(messageId);
    }
    this.saveMessageReactions();
  }

  toggleDislike(messageId: string): void {
    if (this.dislikedMessages.has(messageId)) {
      this.dislikedMessages.delete(messageId);
    } else {
      // Remove from liked if it was there
      this.likedMessages.delete(messageId);
      this.dislikedMessages.add(messageId);
    }
    this.saveMessageReactions();
  }

  openShareModal(message: ChatMessage): void {
    this.sharedMessage = message;
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.sharedMessage = null;
  }

  copyMessage(message: ChatMessage): void {
    try {
      const messageText = message.isUser
        ? message.content
        : this.getMessageBlocks(message).body;
      const messageHtml = message.isUser
        ? message.content
        : formatAyaProse(messageText, message.id);
      
      // Wrap HTML in a div with white-space preservation
      const formattedHtml = `<div style="white-space: pre-wrap; font-family: inherit;">${messageHtml}</div>`;
      
      // Copy both HTML and plain text to clipboard
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([formattedHtml], { type: 'text/html' }),
        'text/plain': new Blob([messageText], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem]).then(() => {
        this.showCopyToast = true;
        
        // Clear any existing timeout
        if (this.copyToastTimeout) {
          clearTimeout(this.copyToastTimeout);
        }
        
        // Hide toast after 2 seconds
        this.copyToastTimeout = setTimeout(() => {
          this.showCopyToast = false;
        }, 2000);
      }).catch((error) => {
        console.error('Error copying formatted content:', error);
        // Fallback to plain text if formatted copy fails
        navigator.clipboard.writeText(messageText);
      });
    } catch (error) {
      console.error('Error copying message:', error);
      // Fallback to plain text
      navigator.clipboard.writeText(message.content);
    }
  }

  shareToPlatform(platform: string): void {
    if (!this.sharedMessage) return;

    const messageText = this.sharedMessage.content;
    const shareUrl = window.location.href; // You might want to generate a proper share URL

    switch (platform) {
      case 'copy':
        const messageHtml = this.formatMessage(this.sharedMessage.content);
        
        // Wrap HTML in a div with white-space preservation
        const formattedHtml = `<div style="white-space: pre-wrap; font-family: inherit;">${messageHtml}</div>`;
        
        // Copy both HTML and plain text to clipboard
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([formattedHtml], { type: 'text/html' }),
          'text/plain': new Blob([messageText], { type: 'text/plain' })
        });

        navigator.clipboard.write([clipboardItem]).then(() => {
          this.showCopyToast = true;
          
          // Clear any existing timeout
          if (this.copyToastTimeout) {
            clearTimeout(this.copyToastTimeout);
          }
          
          // Hide toast after 2 seconds
          this.copyToastTimeout = setTimeout(() => {
            this.showCopyToast = false;
          }, 2000);
        }).catch((error) => {
          console.error('Error copying formatted content:', error);
          // Fallback to plain text if formatted copy fails
          navigator.clipboard.writeText(messageText).then(() => {
            this.showCopyToast = true;
            if (this.copyToastTimeout) {
              clearTimeout(this.copyToastTimeout);
            }
            this.copyToastTimeout = setTimeout(() => {
              this.showCopyToast = false;
            }, 2000);
          });
        });
        this.closeShareModal();
        break;
        
      case 'email':
        const emailSubject = encodeURIComponent('Check out this AI chat response');
        const emailBody = encodeURIComponent(`${messageText}\n\nShared from: ${shareUrl}`);
        window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`);
        break;
        
      case 'twitter':
        const twitterText = encodeURIComponent(`${messageText}\n\nShared from: ${shareUrl}`);
        window.open(`https://twitter.com/intent/tweet?text=${twitterText}`, '_blank');
        break;
        
      case 'facebook':
        const facebookUrl = encodeURIComponent(shareUrl);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${facebookUrl}`, '_blank');
        break;
        
      case 'reddit':
        const redditTitle = encodeURIComponent('AI Chat Response');
        const redditText = encodeURIComponent(`${messageText}\n\nShared from: ${shareUrl}`);
        window.open(`https://www.reddit.com/submit?title=${redditTitle}&text=${redditText}`, '_blank');
        break;
    }
    
    this.closeShareModal();
  }

  // Context menu methods
  onMessageContextMenu(event: MouseEvent, message: ChatMessage): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuMessage = message;

    // Get selected text and HTML
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && selection.toString()) {
      // User selected text
      this.selectedTextForContext = selection.toString();
      
      // Get HTML of selection
      const range = selection.getRangeAt(0);
      const container = document.createElement('div');
      container.appendChild(range.cloneContents());
      this.selectedHtmlForContext = container.innerHTML;
    } else {
      // No selection, use entire message content
      this.selectedTextForContext = message.content;
      
      // Get formatted message content (HTML)
      const messageElement = (event.target as HTMLElement).closest('.message-content');
      if (messageElement) {
        this.selectedHtmlForContext = messageElement.innerHTML;
      } else {
        this.selectedHtmlForContext = message.content;
      }
    }

    // Calculate position
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.showContextMenu = true;

    // Add click listener to close context menu when clicking elsewhere
    setTimeout(() => {
      document.addEventListener('click', this.closeContextMenu.bind(this));
    }, 0);
  }

  closeContextMenu(): void {
    this.showContextMenu = false;
    document.removeEventListener('click', this.closeContextMenu.bind(this));
  }

  copyFromContextMenu(): void {
    try {
      // Wrap HTML in a div with white-space preservation
      const formattedHtml = `<div style="white-space: pre-wrap; font-family: inherit;">${this.selectedHtmlForContext}</div>`;
      
      // Copy both HTML and plain text to clipboard
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([formattedHtml], { type: 'text/html' }),
        'text/plain': new Blob([this.selectedTextForContext], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem]).then(() => {
        this.showCopyToast = true;
        
        // Clear any existing timeout
        if (this.copyToastTimeout) {
          clearTimeout(this.copyToastTimeout);
        }
        
        // Hide toast after 2 seconds
        this.copyToastTimeout = setTimeout(() => {
          this.showCopyToast = false;
        }, 2000);
      }).catch((error) => {
        console.error('Error copying formatted content:', error);
        // Fallback to plain text if formatted copy fails
        navigator.clipboard.writeText(this.selectedTextForContext);
      });
    } catch (error) {
      console.error('Error copying text:', error);
      // Fallback to plain text
      navigator.clipboard.writeText(this.selectedTextForContext);
    }
    this.closeContextMenu();
  }

  async pasteFromContextMenu(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      // Add the pasted text to the input field
      this.userMessage += text;
      // Adjust textarea height
      setTimeout(() => this.adjustTextareaHeight(), 0);
    } catch (error) {
      console.error('Error pasting text:', error);
    }
    this.closeContextMenu();
  }
}
